import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, X, FileText, ChevronRight, CheckCircle2, Bell, Trash2, RefreshCw } from 'lucide-react';
import { PetSchedule, ImmunizationSchedule, CreateScheduleRequest, ImmunizationTemplate } from '../../types/immunization-schedule';
import {
    getPetSchedule,
    rescheduleScheduleEntry,
    cancelScheduleEntry,
    createScheduleEntry,
    getImmunizationTemplates,
    applyImmunizationTemplate
} from '../../utils/immunizationScheduleApi';
import { getPracticeTemplates, applyPracticeTemplate } from '../../utils/practiceImmunizationTemplateApi';
import { getImmunizationCatalog } from '../../utils/immunizationCatalogApi';
import { ImmunizationCatalogItem } from '../../types/immunization-catalog';
import { useAuth } from '../../contexts/AuthContext';
import { ImmunizationFormModal } from './ImmunizationFormModal';
import CreateReminderModal from '../pets/CreateReminderModal';
import DeleteReminderModal from '../pets/DeleteReminderModal';
import { Reminder } from '../../types/reminder';
import { API_ENDPOINTS } from '../../config/api';

interface ImmunizationScheduleViewProps {
    petId: string;
    petName?: string;
    onRefresh?: () => void;
}

// Extended template type to track if it's a practice template
interface CombinedTemplate extends ImmunizationTemplate {
    isPracticeTemplate?: boolean;
}

export function ImmunizationScheduleView({
    petId,
    petName,
    onRefresh
}: ImmunizationScheduleViewProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [schedule, setSchedule] = useState<PetSchedule | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'upcoming' | 'overdue'>('upcoming');

    // Add Modal State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    // Helper to get today's date as YYYY-MM-DD
    const getTodayDate = () => new Date().toISOString().split('T')[0];
    
    const [newDoseData, setNewDoseData] = useState<Partial<CreateScheduleRequest>>({
        immunization_name: '',
        scheduled_date: getTodayDate(),
        notes: ''
    });

    // Template Modal State
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [templates, setTemplates] = useState<CombinedTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [templateStartDate, setTemplateStartDate] = useState<string>(new Date().toISOString().split('T')[0]);

    // Reschedule Modal State
    const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
    const [rescheduleItem, setRescheduleItem] = useState<ImmunizationSchedule | null>(null);
    const [rescheduleDate, setRescheduleDate] = useState<string>('');
    const [rescheduleReason, setRescheduleReason] = useState<string>('');

    // Cancel Modal State
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [cancelItem, setCancelItem] = useState<ImmunizationSchedule | null>(null);
    const [cancelReason, setCancelReason] = useState<string>('');

    const [submitting, setSubmitting] = useState(false);

    // Complete Modal State
    const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
    const [completeItem, setCompleteItem] = useState<ImmunizationSchedule | null>(null);

    // Reminder State
    const [reminders, setReminders] = useState<Reminder[]>([]);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [selectedScheduleForReminder, setSelectedScheduleForReminder] = useState<ImmunizationSchedule | null>(null);
    const [showDeleteReminderModal, setShowDeleteReminderModal] = useState(false);
    const [reminderToDelete, setReminderToDelete] = useState<Reminder | null>(null);
    const [deletingReminder, setDeletingReminder] = useState(false);

    // Catalog State for Schedule Immunization dropdown
    const [catalogItems, setCatalogItems] = useState<ImmunizationCatalogItem[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const openCompleteModal = (item: ImmunizationSchedule) => {
        setCompleteItem(item);
        setIsCompleteModalOpen(true);
    };

    const handleCompleteSuccess = async () => {
        setIsCompleteModalOpen(false);
        setCompleteItem(null);
        await fetchSchedule();
        onRefresh?.();
    };

    useEffect(() => {
        fetchSchedule();
        fetchReminders();
    }, [petId]);

    // Handle ESC key to close modals
    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isAddModalOpen) setIsAddModalOpen(false);
                if (isTemplateModalOpen) setIsTemplateModalOpen(false);
                if (isRescheduleModalOpen) setIsRescheduleModalOpen(false);
                if (isCancelModalOpen) setIsCancelModalOpen(false);
                if (isCompleteModalOpen) setIsCompleteModalOpen(false);
                if (showReminderModal) setShowReminderModal(false);
                if (showDeleteReminderModal) setShowDeleteReminderModal(false);
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [isAddModalOpen, isTemplateModalOpen, isRescheduleModalOpen, isCancelModalOpen, isCompleteModalOpen, showReminderModal, showDeleteReminderModal]);

    const fetchReminders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ENDPOINTS.REMINDERS.BY_PET(petId), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setReminders(data);
            }
        } catch (error) {
            console.error('Error fetching reminders:', error);
        }
    };

    const getLinkedReminderForSchedule = (scheduleItem: ImmunizationSchedule) => {
        return reminders.find(r =>
            r.status !== 'cancelled' && (
                // Check if reminder notes contain the schedule ID
                (r.notes && r.notes.includes(`[Linked Schedule ID: ${scheduleItem.id}]`)) ||
                // Fallback: match by date and similar name
                (r.due_date && scheduleItem.scheduled_date &&
                    new Date(r.due_date).toISOString().split('T')[0] === new Date(scheduleItem.scheduled_date).toISOString().split('T')[0] &&
                    r.reminder_type?.name.toLowerCase().includes(scheduleItem.immunization_name.toLowerCase()))
            )
        );
    };

    const handleScheduleBellClick = (scheduleItem: ImmunizationSchedule) => {
        // If this schedule has template-based reminders, navigate to global template settings
        if (scheduleItem.has_template_reminders) {
            navigate('/settings/immunization-catalog?tab=templates');
            return;
        }

        // Check if there's already an individual reminder for this schedule
        const existingReminder = getLinkedReminderForSchedule(scheduleItem);
        if (existingReminder) {
            // If reminder exists, offer to delete it
            setReminderToDelete(existingReminder);
            setShowDeleteReminderModal(true);
        } else {
            // Open modal to create a reminder for this specific dose
            setSelectedScheduleForReminder(scheduleItem);
            setShowReminderModal(true);
        }
    };

    const handleConfirmDeleteReminder = async () => {
        if (!reminderToDelete) return;

        setDeletingReminder(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ENDPOINTS.REMINDERS.CANCEL(reminderToDelete.id), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                await fetchReminders();
                setShowDeleteReminderModal(false);
                setReminderToDelete(null);
            } else {
                alert('Failed to delete reminder');
            }
        } catch (error) {
            console.error('Error deleting reminder:', error);
            alert('Failed to delete reminder');
        } finally {
            setDeletingReminder(false);
        }
    };

    const fetchSchedule = async () => {
        try {
            setLoading(true);
            const data = await getPetSchedule(petId, false);
            setSchedule(data);
            setError(null);

            // Auto-switch to overdue tab if there are overdue items
            if (data.overdue.length > 0) {
                setActiveTab('overdue');
            }
        } catch (err) {
            console.error('Failed to fetch schedule:', err);
            setError('Failed to load immunization schedule');
        } finally {
            setLoading(false);
        }
    };

    const fetchCatalogItems = async () => {
        if (!user?.practice_id) return;
        try {
            setCatalogLoading(true);
            const items = await getImmunizationCatalog(user.practice_id, undefined, true);
            setCatalogItems(items);
        } catch (err) {
            console.error('Failed to fetch catalog items:', err);
        } finally {
            setCatalogLoading(false);
        }
    };

    const handleOpenAddModal = async () => {
        setIsAddModalOpen(true);
        if (catalogItems.length === 0) {
            await fetchCatalogItems();
        }
    };

    const handleAddDose = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newDoseData.immunization_name || !newDoseData.scheduled_date) return;

        try {
            setSubmitting(true);
            await createScheduleEntry(petId, {
                pet_id: petId,
                immunization_name: newDoseData.immunization_name,
                scheduled_date: newDoseData.scheduled_date,
                notes: newDoseData.notes,
                series_number: newDoseData.series_number
            });

            setIsAddModalOpen(false);
            setNewDoseData({ immunization_name: '', scheduled_date: getTodayDate(), notes: '' });
            await fetchSchedule();
            onRefresh?.();
        } catch (err) {
            console.error(err);
            setError('Failed to create schedule entry');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenTemplateModal = async () => {
        try {
            setLoading(true);

            // Fetch standard templates
            const standardTemplates = await getImmunizationTemplates();

            // Fetch practice templates if user has a practice
            let practiceTemplates: CombinedTemplate[] = [];
            if (user?.practice_id) {
                try {
                    const practiceData = await getPracticeTemplates(user.practice_id, { activeOnly: true });
                    practiceTemplates = practiceData.map(pt => ({
                        id: pt.id,
                        name: pt.name,
                        species: pt.species || 'All',
                        description: pt.description || '',
                        items: [], // Items loaded separately if needed
                        isPracticeTemplate: true
                    }));
                } catch (e) {
                    console.warn('Failed to load practice templates:', e);
                }
            }

            // Combine: practice templates first (custom), then standard
            setTemplates([...practiceTemplates, ...standardTemplates]);
            setIsTemplateModalOpen(true);
        } catch (err) {
            console.error(err);
            setError('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTemplateId) return;

        const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

        // Standard templates require a start date
        if (!selectedTemplate?.isPracticeTemplate && !templateStartDate) return;

        try {
            setSubmitting(true);

            if (selectedTemplate?.isPracticeTemplate) {
                // Use practice template endpoint - uses pet's birthday for age-based scheduling
                await applyPracticeTemplate(petId, {
                    template_id: selectedTemplateId
                    // No start_date needed - backend uses pet's birthdate
                });
            } else {
                // Use standard template endpoint with start date
                await applyImmunizationTemplate(petId, {
                    template_id: selectedTemplateId,
                    start_date: templateStartDate
                });
            }

            setIsTemplateModalOpen(false);
            setSelectedTemplateId('');
            await fetchSchedule();
            onRefresh?.();
        } catch (err) {
            console.error(err);
            setError('Failed to apply template');
        } finally {
            setSubmitting(false);
        }
    };

    const openRescheduleModal = (item: ImmunizationSchedule) => {
        setRescheduleItem(item);
        setRescheduleDate(item.scheduled_date);
        setRescheduleReason('');
        setIsRescheduleModalOpen(true);
    };

    const handleReschedule = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rescheduleItem || !rescheduleDate) return;

        try {
            setSubmitting(true);
            await rescheduleScheduleEntry(rescheduleItem.id, {
                new_date: rescheduleDate,
                reason: rescheduleReason || undefined
            });
            setIsRescheduleModalOpen(false);
            setRescheduleItem(null);
            await fetchSchedule();
            onRefresh?.();
        } catch (err) {
            console.error(err);
            setError('Failed to reschedule');
        } finally {
            setSubmitting(false);
        }
    };

    const openCancelModal = (item: ImmunizationSchedule) => {
        setCancelItem(item);
        setCancelReason('');
        setIsCancelModalOpen(true);
    };

    const handleCancel = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!cancelItem) return;

        try {
            setSubmitting(true);
            await cancelScheduleEntry(cancelItem.id, cancelReason ? { reason: cancelReason } : {});
            setIsCancelModalOpen(false);
            setCancelItem(null);
            await fetchSchedule();
            onRefresh?.();
        } catch (err) {
            console.error(err);
            setError('Failed to cancel');
        } finally {
            setSubmitting(false);
        }
    };

    // Catch Up: Realign all overdue vaccines relative to today
    const handleCatchUp = async () => {
        if (!schedule || schedule.overdue.length === 0) return;

        try {
            setSubmitting(true);

            // Parse date string as local date (YYYY-MM-DD format)
            const parseLocalDate = (dateStr: string): Date => {
                const [year, month, day] = dateStr.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            // Format date as YYYY-MM-DD without UTC conversion
            const formatLocalDate = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Find the earliest scheduled date among overdue items
            const sortedOverdue = [...schedule.overdue].sort(
                (a, b) => parseLocalDate(a.scheduled_date).getTime() - parseLocalDate(b.scheduled_date).getTime()
            );
            const earliestDate = parseLocalDate(sortedOverdue[0].scheduled_date);

            // Calculate offset from earliest date to today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const offsetDays = Math.round((today.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24));

            // Only catch up if actually overdue (offset > 0), add 7 days padding
            const paddingDays = 7;
            const adjustedOffset = offsetDays > 0 ? offsetDays + paddingDays : paddingDays;

            // Reschedule each overdue item with the adjusted offset (preserving relative intervals)
            for (const item of schedule.overdue) {
                const originalDate = parseLocalDate(item.scheduled_date);
                const newDate = new Date(originalDate);
                newDate.setDate(newDate.getDate() + adjustedOffset);
                const newDateStr = formatLocalDate(newDate);

                await rescheduleScheduleEntry(item.id, {
                    new_date: newDateStr,
                    reason: `Catch-up: realigned from ${item.scheduled_date}`
                });
            }

            await fetchSchedule();
            onRefresh?.();
        } catch (err) {
            console.error('Catch-up failed:', err);
            setError('Failed to catch up schedule');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        // Use UTC timezone to avoid date shift due to local timezone offset
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const getDaysUntil = (dateString: string): number => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Parse YYYY-MM-DD as LOCAL date, not UTC
        const [year, month, day] = dateString.split('-').map(Number);
        const scheduled = new Date(year, month - 1, day); // month is 0-indexed
        const diffTime = scheduled.getTime() - today.getTime();
        return Math.round(diffTime / (1000 * 60 * 60 * 24));
    };

    const getDueLabel = (dateString: string, isOverdue: boolean): string => {
        const daysUntil = getDaysUntil(dateString);
        if (isOverdue) {
            return `${Math.abs(daysUntil)} days overdue`;
        }
        if (daysUntil === 0) return 'Due today';
        if (daysUntil === 1) return 'Due tomorrow';
        return `${daysUntil} days away`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
                <button
                    onClick={() => { setError(null); fetchSchedule(); }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                    Try again
                </button>
            </div>
        );
    }

    if (!schedule) {
        return null;
    }

    const hasOverdue = schedule.overdue.length > 0;
    const hasUpcoming = schedule.upcoming.length > 0;
    const currentItems = activeTab === 'upcoming' ? schedule.upcoming : schedule.overdue;
    const isOverdueTab = activeTab === 'overdue';

    return (
        <div className="mt-8 px-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Immunization Schedule</h3>
                <div className="flex gap-2">
                    {hasOverdue && (
                        <button
                            onClick={handleCatchUp}
                            disabled={submitting}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
                            title="Realign all overdue vaccines starting from today"
                        >
                            <RefreshCw className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
                            Catch Up
                        </button>
                    )}
                    <button
                        onClick={handleOpenTemplateModal}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <FileText className="w-4 h-4" />
                        Apply Template
                    </button>
                    <button
                        onClick={handleOpenAddModal}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Schedule Immunization
                    </button>
                </div>
            </div>

            {/* Schedule Card */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Tabs */}
                <div className="flex bg-gray-50 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('upcoming')}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'upcoming'
                            ? 'border-blue-600 text-blue-600 bg-white'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Upcoming
                        {hasUpcoming && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                                {schedule.upcoming.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('overdue')}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'overdue'
                            ? 'border-red-600 text-red-600 bg-white'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Overdue
                        {hasOverdue && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded">
                                {schedule.overdue.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Table */}
                {currentItems.length > 0 ? (
                    <div>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Immunization
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Scheduled Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Alert
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Administer
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Reschedule
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Delete
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems.map((item) => {
                                    const isOrdered = item.status === 'ordered';
                                    return (
                                        <tr
                                            key={item.id}
                                            className={`${isOrdered
                                                    ? 'bg-emerald-50 dark:bg-emerald-900/30 border-l-4 border-l-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'
                                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`text-sm font-medium ${isOrdered ? 'text-emerald-900 dark:text-emerald-100' : 'text-gray-900 dark:text-gray-100'}`}>
                                                    {item.immunization_name}
                                                </span>
                                                {isOrdered && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-600 text-white">
                                                        READY
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                {formatDate(item.scheduled_date)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {isOrdered ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100">
                                                        Ordered Today
                                                    </span>
                                                ) : (
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isOverdueTab
                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                        : getDaysUntil(item.scheduled_date) <= 7
                                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        }`}>
                                                        {getDueLabel(item.scheduled_date, isOverdueTab)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                {(() => {
                                                    const hasIndividualReminder = getLinkedReminderForSchedule(item);
                                                    const hasTemplateReminder = item.has_template_reminders;
                                                    const hasAnyReminder = hasIndividualReminder || hasTemplateReminder;

                                                    // Template reminders = green (global), Individual = blue (local)
                                                    const bellClass = hasTemplateReminder
                                                        ? 'text-green-600 bg-green-50 hover:bg-green-100'
                                                        : hasIndividualReminder
                                                            ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                                                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50';

                                                    const bellTitle = hasTemplateReminder
                                                        ? 'Template reminder active - click to manage global settings'
                                                        : hasIndividualReminder
                                                            ? 'Reminder active - click to manage'
                                                            : 'Create reminder for this dose';

                                                    return (
                                                        <button
                                                            onClick={() => handleScheduleBellClick(item)}
                                                            className={`p-2 rounded transition-colors ${bellClass}`}
                                                            title={bellTitle}
                                                        >
                                                            <Bell className={`w-4 h-4 ${hasAnyReminder ? 'fill-current' : ''}`} />
                                                        </button>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <button
                                                    onClick={() => openCompleteModal(item)}
                                                    className={`p-2 rounded transition-colors ${isOrdered
                                                            ? 'text-white bg-emerald-600 hover:bg-emerald-700'
                                                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/50'
                                                        }`}
                                                    title={isOrdered ? "Administer Now" : "Mark as Administered"}
                                                >
                                                    <CheckCircle2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <button
                                                    onClick={() => openRescheduleModal(item)}
                                                    className="p-2 rounded transition-colors text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                                    title="Reschedule"
                                                >
                                                    <Calendar className="w-4 h-4" />
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <button
                                                    onClick={() => openCancelModal(item)}
                                                    className="p-2 rounded transition-colors text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center bg-gray-50">
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
                        <p className="text-gray-900 font-medium">
                            {isOverdueTab ? 'No overdue immunizations!' : 'No upcoming immunizations scheduled'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                            {isOverdueTab ? 'All vaccines are up to date' : 'Apply a template or add doses manually'}
                        </p>
                        {!isOverdueTab && (
                            <div className="flex justify-center gap-3 mt-4">
                                <button
                                    onClick={handleOpenTemplateModal}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Apply a Template
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                    onClick={handleOpenAddModal}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Schedule Manually
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Reschedule Modal */}
            {isRescheduleModalOpen && rescheduleItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">Reschedule Immunization</h3>
                            <button
                                onClick={() => setIsRescheduleModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleReschedule} className="p-6 space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium text-gray-900">{rescheduleItem.immunization_name}</span>
                                    {rescheduleItem.series_number && ` (Dose ${rescheduleItem.series_number})`}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Currently scheduled: {formatDate(rescheduleItem.scheduled_date)}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    New Date
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={rescheduleDate}
                                    onChange={e => setRescheduleDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason (Optional)
                                </label>
                                <textarea
                                    value={rescheduleReason}
                                    onChange={e => setRescheduleReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    rows={2}
                                    placeholder="Reason for rescheduling..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsRescheduleModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Saving...' : 'Reschedule'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isCancelModalOpen && cancelItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Trash2 className="w-5 h-5 text-red-600" />
                                Delete Scheduled Dose
                            </h3>
                            <button
                                onClick={() => setIsCancelModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleCancel} className="p-6 space-y-4">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-sm text-gray-700">
                                    Are you sure you want to delete this scheduled dose?
                                </p>
                                <p className="text-sm font-medium text-gray-900 mt-2">
                                    {cancelItem.immunization_name}
                                    {cancelItem.series_number && ` (Dose ${cancelItem.series_number})`}
                                </p>
                                <p className="text-sm text-gray-500">
                                    Scheduled: {formatDate(cancelItem.scheduled_date)}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason for Deletion (Optional)
                                </label>
                                <textarea
                                    value={cancelReason}
                                    onChange={e => setCancelReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    rows={2}
                                    placeholder="Why is this dose being deleted?"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsCancelModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Keep Scheduled
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {submitting ? 'Deleting...' : 'Delete Dose'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Schedule Immunization Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">Schedule Immunization</h3>
                            <button
                                onClick={() => setIsAddModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAddDose} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Immunization Name
                                </label>
                                {catalogLoading ? (
                                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                                        Loading catalog...
                                    </div>
                                ) : catalogItems.length > 0 ? (
                                    <select
                                        required
                                        value={newDoseData.immunization_name}
                                        onChange={e => setNewDoseData({ ...newDoseData, immunization_name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Select an immunization...</option>
                                        {catalogItems.map(item => (
                                            <option key={item.id} value={item.name}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        required
                                        value={newDoseData.immunization_name}
                                        onChange={e => setNewDoseData({ ...newDoseData, immunization_name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g. Rabies, DHPP"
                                    />
                                )}
                                {catalogItems.length === 0 && !catalogLoading && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        No catalog items found. <a href="/settings/immunization-catalog" className="text-blue-600 hover:underline">Add items to your catalog</a>
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Scheduled Date
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={newDoseData.scheduled_date}
                                    onChange={e => setNewDoseData({ ...newDoseData, scheduled_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Dose Number (Optional)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={newDoseData.series_number || ''}
                                    onChange={e => setNewDoseData({ ...newDoseData, series_number: e.target.value ? parseInt(e.target.value) : undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g. 2 for 2nd dose"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={newDoseData.notes || ''}
                                    onChange={e => setNewDoseData({ ...newDoseData, notes: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    rows={2}
                                    placeholder="Any special instructions..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Scheduling...' : 'Schedule Dose'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Template Modal */}
            {isTemplateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">Apply Immunization Template</h3>
                            <button
                                onClick={() => setIsTemplateModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleApplyTemplate} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Select Template
                                </label>
                                <select
                                    required
                                    value={selectedTemplateId}
                                    onChange={e => setSelectedTemplateId(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">-- Select a protocol --</option>
                                    {templates.filter(t => t.isPracticeTemplate).length > 0 && (
                                        <optgroup label="Your Practice Templates">
                                            {templates.filter(t => t.isPracticeTemplate).map(t => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} ({t.species})
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    <optgroup label="Standard Templates">
                                        {templates.filter(t => !t.isPracticeTemplate).map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} ({t.species})
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                                {selectedTemplateId && (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {templates.find(t => t.id === selectedTemplateId)?.description}
                                    </p>
                                )}
                            </div>

                            {/* Only show Start Date for standard templates - practice templates use pet's birthday */}
                            {selectedTemplateId && !templates.find(t => t.id === selectedTemplateId)?.isPracticeTemplate && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={templateStartDate}
                                        onChange={e => setTemplateStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        The date of the first dose. Subsequent doses will be scheduled relative to this date.
                                    </p>
                                </div>
                            )}

                            {selectedTemplateId && templates.find(t => t.id === selectedTemplateId)?.isPracticeTemplate && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <p className="text-sm text-blue-800">
                                        Vaccines will be scheduled based on the pet's birthday (age-based scheduling).
                                    </p>
                                </div>
                            )}

                            {/* Preview - only for standard templates (practice templates don't have items loaded) */}
                            {selectedTemplateId && !templates.find(t => t.id === selectedTemplateId)?.isPracticeTemplate && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-48 overflow-y-auto">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                        Schedule Preview
                                    </h4>
                                    <div className="space-y-2">
                                        {templates.find(t => t.id === selectedTemplateId)?.items.map((item, idx) => (
                                            <div key={idx} className="flex items-start gap-2 text-sm">
                                                <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                                                <div>
                                                    <span className="font-medium text-gray-900">{item.name}</span>
                                                    <span className="text-gray-500 mx-1">•</span>
                                                    <span className="text-gray-600">
                                                        {item.age_weeks != null
                                                            ? `@ ${item.age_weeks} weeks old`
                                                            : item.offset_days === 0
                                                                ? 'Start Date'
                                                                : `+${item.offset_days} days`
                                                        }
                                                    </span>
                                                    {item.notes && (
                                                        <p className="text-xs text-gray-500">{item.notes}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsTemplateModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || !selectedTemplateId}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Applying...' : 'Apply Template'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Complete Modal */}
            {isCompleteModalOpen && completeItem && (
                <ImmunizationFormModal
                    isOpen={isCompleteModalOpen}
                    onClose={() => setIsCompleteModalOpen(false)}
                    onSuccess={handleCompleteSuccess}
                    petId={petId}
                    scheduleId={completeItem.id}
                    initialValues={{
                        name: completeItem.immunization_name,
                        note: completeItem.notes || undefined,
                        administered_date: new Date().toLocaleDateString('en-CA') // YYYY-MM-DD in local time
                    }}
                    catalogItemId={completeItem.catalog_item_id}
                />
            )}

            {/* Create Reminder Modal */}
            {showReminderModal && selectedScheduleForReminder && (
                <CreateReminderModal
                    isOpen={showReminderModal}
                    onClose={() => {
                        setShowReminderModal(false);
                        setSelectedScheduleForReminder(null);
                    }}
                    onSuccess={() => {
                        setShowReminderModal(false);
                        setSelectedScheduleForReminder(null);
                        fetchReminders();
                    }}
                    petId={petId}
                    initialValues={{
                        title: `${selectedScheduleForReminder.immunization_name} Due`,
                        due_date: selectedScheduleForReminder.scheduled_date.split('T')[0],
                        notes: `[Linked Schedule ID: ${selectedScheduleForReminder.id}]`
                    }}
                />
            )}

            {/* Delete Reminder Modal */}
            <DeleteReminderModal
                isOpen={showDeleteReminderModal}
                onClose={() => {
                    setShowDeleteReminderModal(false);
                    setReminderToDelete(null);
                }}
                onConfirm={handleConfirmDeleteReminder}
                reminderName={reminderToDelete?.title || reminderToDelete?.reminder_type?.name || 'this reminder'}
                loading={deletingReminder}
            />
        </div>
    );
}
